import authService from './authService.js';

// Always use absolute URLs based on current origin to work with nginx reverse proxy
// This ensures all requests go through: nginx → API Gateway → microservices
const API_BASE_URL = `${window.location.origin}/api`;

class TenantService {
  async getAllTenants(page = 0, size = 20, sortBy = 'name', sortDir = 'asc', filters = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy,
        sortDir
      });

      if (filters.activeOnly !== undefined) {
        params.append('activeOnly', filters.activeOnly.toString());
      }
      if (filters.subscriptionType) {
        params.append('subscriptionType', filters.subscriptionType);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch tenants');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching tenants:', error);
      throw error;
    }
  }

  async getTenantById(id) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch tenant');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching tenant:', error);
      throw error;
    }
  }

  async createTenant(tenantData) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants`, {
        method: 'POST',
        body: JSON.stringify(tenantData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create tenant');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating tenant:', error);
      throw error;
    }
  }

  async updateTenant(id, tenantData) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${id}`, {
        method: 'PUT',
        body: JSON.stringify(tenantData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update tenant');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  }

  async deleteTenant(id) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete tenant');
      }

      return true;
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  }

  async getTenantUsageStats(id) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${id}/usage-stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch tenant usage stats');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching tenant usage stats:', error);
      throw error;
    }
  }

  async getTenantDashboardStats() {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/dashboard-stats`);
      if (!response.ok) {
        throw new Error('Failed to fetch tenant dashboard stats');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching tenant dashboard stats:', error);
      throw error;
    }
  }

  async updateTenantSubscription(id, subscriptionData) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${id}/subscription`, {
        method: 'PUT',
        body: JSON.stringify(subscriptionData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update tenant subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating tenant subscription:', error);
      throw error;
    }
  }

  async provisionAdminAccess(tenantId, userEmail) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${tenantId}/provision-admin`, {
        method: 'POST',
        body: JSON.stringify({ userEmail })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to provision admin access');
      }

      return await response.json();
    } catch (error) {
      console.error('Error provisioning admin access:', error);
      throw error;
    }
  }

  async revokeAdminAccess(tenantId, userEmail) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/tenants/${tenantId}/revoke-admin`, {
        method: 'DELETE',
        body: JSON.stringify({ userEmail })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to revoke admin access');
      }

      return await response.json();
    } catch (error) {
      console.error('Error revoking admin access:', error);
      throw error;
    }
  }

  async getTenantAdmins(tenantId) {
    try {
      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/users/search?tenantId=${tenantId}&roles=ROLE_ADMIN`);
      if (!response.ok) {
        throw new Error('Failed to fetch tenant admins');
      }
      const result = await response.json();
      return result.content || [];
    } catch (error) {
      console.error('Error fetching tenant admins:', error);
      throw error;
    }
  }

  async searchUsers(tenantId, query = '', role = '') {
    try {
      const params = new URLSearchParams();
      if (tenantId) params.append('tenantId', tenantId);
      if (query) params.append('query', query);
      if (role) params.append('role', role);

      const response = await authService.authenticatedFetch(`${API_BASE_URL}/super-admin/users/search?${params}`);
      if (!response.ok) {
        throw new Error('Failed to search users');
      }
      const result = await response.json();
      return result.content || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }
}

export default new TenantService();
